import { useState, ChangeEvent } from "react";
import { motion } from "framer-motion";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";

interface FilterItem {
	label: string;
	count: number;
	value: string | number;
	checked: boolean;
}

interface FilterGroupProps {
	title: string;
	items: FilterItem[];
	onToggle: (value: string | number) => void;
	onSelectAll?: (values: (string | number)[]) => void;
	onUnselectAll?: (values: (string | number)[]) => void;
	selectedCount?: number;
	renderItem?: (item: FilterItem) => React.ReactNode;
}

const MAX_SHOWN_INITIALLY = 5;
const MIN_SHOWN_SEARCH = 10;

export const FilterGroup: React.FC<FilterGroupProps> = ({
	title,
	items,
	onToggle,
	onSelectAll,
	onUnselectAll,
	selectedCount = 0,
	renderItem,
}) => {
	const [expanded, setExpanded] = useState(false);
	const [searchTerm, setSearchTerm] = useState("");

	const filteredItems = items.filter(item => item.label.toLowerCase().includes(searchTerm.toLowerCase()));

	const alwaysVisible = filteredItems.slice(0, MAX_SHOWN_INITIALLY);
	const hiddenItems = filteredItems.slice(MAX_SHOWN_INITIALLY);

	const showSelectedCount = selectedCount > 0;

	const handleSelectAll = () => {
		if (onSelectAll) onSelectAll(filteredItems.map(i => i.value));
	};

	const handleUnselectAll = () => {
		if (onUnselectAll) onUnselectAll(filteredItems.map(i => i.value));
	};

	return (
		<Accordion type="single" collapsible className="w-full" defaultValue={title}>
			<AccordionItem value={title}>
				<AccordionTrigger className="text-xs font-medium flex pt-2 pb-1 items-center gap-2">
					<div className="flex items-center gap-2">
						<span>{title}</span>
						{showSelectedCount && (
							<span className="text-[11px] text-muted-foreground">({selectedCount})</span>
						)}
						<span className="text-[10px] text-gray-400 ml-auto flex gap-1">
							<span
								role="button"
								tabIndex={0}
								onClick={event => {
									event.stopPropagation();
									handleSelectAll();
								}}
								onKeyDown={event => {
									if (event.key === "Enter" || event.key === " ") {
										event.stopPropagation();
										event.preventDefault();
										handleSelectAll();
									}
								}}
								className="hover:underline cursor-pointer"
							>
								All
							</span>
							<span>/</span>
							<span
								role="button"
								tabIndex={0}
								onClick={event => {
									event.stopPropagation();
									handleUnselectAll();
								}}
								onKeyDown={event => {
									if (event.key === "Enter" || event.key === " ") {
										event.stopPropagation();
										event.preventDefault();
										handleUnselectAll();
									}
								}}
								className="hover:underline cursor-pointer"
							>
								None
							</span>
						</span>
					</div>
				</AccordionTrigger>

				<AccordionContent className="space-y-1.5">
					{items.length > MIN_SHOWN_SEARCH && (
						<Input
							type="text"
							placeholder="Search..."
							value={searchTerm}
							onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
							className="text-xs h-7 flex-1"
						/>
					)}

					{alwaysVisible.map(item => (
						<div className="flex items-center justify-between" key={item.value}>
							<div className="flex items-center gap-1.5">
								<Checkbox
									id={String(item.value)}
									checked={item.checked}
									onCheckedChange={() => onToggle(item.value)}
									className="border-zinc-300 shadow-none h-4 w-4"
								/>
								{renderItem ? (
									renderItem(item)
								) : (
									<label htmlFor={String(item.value)} className="text-xs">
										{item.label}
									</label>
								)}
							</div>
							<span className="text-[10px] text-muted-foreground tabular-nums">{item.count}</span>
						</div>
					))}

					{hiddenItems.length > 0 && (
						<motion.div
							initial={false}
							animate={{ height: expanded ? "auto" : 0, opacity: expanded ? 1 : 0 }}
							transition={{ duration: 0.3 }}
							className="overflow-hidden"
						>
							<div className="space-y-1.5">
								{hiddenItems.map(item => (
									<div className="flex items-center justify-between" key={item.value}>
										<div className="flex items-center gap-1.5">
											<Checkbox
												id={String(item.value)}
												checked={item.checked}
												onCheckedChange={() => onToggle(item.value)}
												className="border-zinc-300 shadow-none h-4 w-4"
											/>
											{renderItem ? (
												renderItem(item)
											) : (
												<label htmlFor={String(item.value)} className="text-xs">
													{item.label}
												</label>
											)}
										</div>
										<span className="text-[10px] text-muted-foreground tabular-nums">
											{item.count}
										</span>
									</div>
								))}
							</div>
						</motion.div>
					)}

					{filteredItems.length > MAX_SHOWN_INITIALLY && (
						<button
							className="text-[11px] text-blue-600 hover:underline"
							onClick={() => setExpanded(prev => !prev)}
						>
							{expanded ? "See less" : `+${filteredItems.length - MAX_SHOWN_INITIALLY} more`}
						</button>
					)}
				</AccordionContent>
			</AccordionItem>
		</Accordion>
	);
};
